import { Handle, Position, useConnection, type Node, type NodeProps } from '@xyflow/react'
import { memo, useEffect, useRef, useState } from 'react'

import { NodeEntity } from '@flowx/designer/shared/entities/node.entity'
import { ActivityNode } from '@flowx/designer/shared/entities/node.types'

import {
  ActivityNodeStyles,
  GraphColors,
  MAX_LABEL_ROWS,
  NodeLabel as NodeLabelStyles,
  UtilityIcons,
} from '../../../bpmn-graph.constants'
import { BpmnNodeData, canStartSequence, nearestTargetPosition } from '../bpmn-graph.mapper'
import { useBpmnDiagramContext } from '../bpmn-diagram.context'
import { useNodeEditingUsers } from '../editing-users.context'
import { TARGET_HANDLE_POSITIONS, targetHandleId } from '../target-handles'
import { BpmnNodeKind, getNodeVisuals, recolorIconSvg, recolorShapeSvg } from '../node-visuals'
import { useRefreshHandleBounds } from '../use-refresh-handle-bounds'
import NodeEditingUsers from './NodeEditingUsers'

const hiddenHandleStyle: React.CSSProperties = {
  opacity: 0,
  width: 1,
  height: 1,
  minWidth: 0,
  minHeight: 0,
  border: 'none',
  background: 'transparent',
}

export const CONNECT_SOURCE_HANDLE_ID = 'connect-source'

export const connectSourceHandleStyle: React.CSSProperties = {
  width: 24,
  height: 24,
  background: 'transparent',
  border: 'none',
  borderRadius: '100%',
  // Shape/icon divs render after the handles — lift the icon so it stays hittable
  zIndex: 1,
}

/** CONNECTOR glyph drawn inside the connect-source handle */
export const ConnectSourceIcon = () => (
  <div
    style={{ width: 24, height: 24, display: 'flex', pointerEvents: 'none' }}
    dangerouslySetInnerHTML={{ __html: UtilityIcons.CONNECTOR }}
  />
)

/**
 * Twin of connectSourceHandleStyle for the target side. One is mounted per port,
 * and the one the connection would land on is the one made visible — the canvas
 * resolves that port from the pointer, not from React Flow's handle proximity.
 */
export const connectTargetHandleStyle: React.CSSProperties = {
  width: 8,
  height: 8,
  background: GraphColors.blue_500,
  border: 'none',
  borderRadius: '100%',
  zIndex: 1,
}

/**
 * Fixed leading so MAX_LABEL_ROWS rows always fit the label box
 * (node height minus padding) — with the browser default the clamped 5 rows
 * outgrow the node and the text spills past its border.
 */
const LABEL_LINE_HEIGHT = 14

function NodeLabel({
  nodeId,
  name,
  editing,
  onCommit,
  onCancel,
  width,
  maxHeight,
}: {
  nodeId: number
  name: string
  editing: boolean
  onCommit: (newName: string) => void
  onCancel: () => void
  width: number
  maxHeight?: number
}) {
  const [value, setValue] = useState(name)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (editing) {
      setValue(name) // discard any leftover text from a prior cancelled edit
      inputRef.current?.focus()
    }
  }, [editing, name])

  if (!editing) {
    return (
      <span
        data-testid={`bpmn-node-label-${nodeId}`}
        style={{
          fontSize: NodeLabelStyles.fontSize,
          fontFamily: NodeLabelStyles.fontFamily,
          fontWeight: NodeLabelStyles.fontWeight,
          color: GraphColors.dark,
          textAlign: 'center',
          overflow: 'hidden',
          display: '-webkit-box',
          WebkitLineClamp: MAX_LABEL_ROWS,
          WebkitBoxOrient: 'vertical',
          wordBreak: 'break-word',
          lineHeight: `${LABEL_LINE_HEIGHT}px`,
          width,
          maxHeight,
        }}
      >
        {name}
      </span>
    )
  }

  return (
    <input
      ref={inputRef}
      data-testid={`bpmn-node-label-input-${nodeId}`}
      value={value}
      onChange={(event) => setValue(event.target.value)}
      onBlur={() => onCommit(value)}
      onKeyDown={(event) => {
        event.stopPropagation()
        if (event.key === 'Enter') {
          onCommit(value)
        }
        if (event.key === 'Escape') {
          onCancel()
        }
      }}
      onClick={(event) => event.stopPropagation()}
      style={{
        fontSize: NodeLabelStyles.fontSize,
        fontFamily: NodeLabelStyles.fontFamily,
        fontWeight: NodeLabelStyles.fontWeight,
        textAlign: 'center',
        width,
        border: `1px solid ${value.trim() ? GraphColors.blue_500 : GraphColors.red_500}`,
        borderRadius: 4,
        outline: 'none',
        background: GraphColors.white,
      }}
    />
  )
}

function BpmnFlowNode({
  id,
  data,
  selected,
  positionAbsoluteX,
  positionAbsoluteY,
}: NodeProps<Node<BpmnNodeData>>) {
  const {
    definition,
    highlighted,
    readonly,
    showReadonlyIcon,
    disabled,
    invalid,
    auditStyles,
    canAddOutgoingSequence,
  } = data
  const { onRenameNode, onShowSubprocessesMenu } = useBpmnDiagramContext()
  const editingUsers = useNodeEditingUsers(definition.id)
  const visuals = getNodeVisuals(definition.type)
  const [editing, setEditing] = useState(false)
  const [hovered, setHovered] = useState(false)
  const connection = useConnection()
  const showConnectSource = canStartSequence(definition.type) && canAddOutgoingSequence
  // Key on the connect handles actually mounted: readonly mounts none of them, so flag changes
  // under it are no-ops
  useRefreshHandleBounds(id, readonly ? 'readonly' : `editable:${showConnectSource}`)
  const connectionPointer = connection.inProgress ? connection.to : null
  // The in-progress connection is currently pointing at THIS node — mirror the
  // source dot on the target border so the user sees where the arrow will land.
  // Snapped within connectionRadius OR anywhere over the node body: either way
  // releasing commits the sequence (onConnectEnd hit-tests the drop point), so
  // both hover states advertise the drop target.
  const isCurrentConnectTarget =
    !!connectionPointer &&
    connection.fromNode?.id !== id &&
    (connection.toNode?.id === id || hovered)
  // Which of the four ports the preview snapped to — the same rule the canvas
  // resolver runs, so the dot appears under the arrowhead and nowhere else.
  const activeTargetPosition =
    isCurrentConnectTarget && connectionPointer
      ? nearestTargetPosition(
          {
            x: positionAbsoluteX,
            y: positionAbsoluteY,
            width: visuals.width,
            height: visuals.height,
          },
          connectionPointer
        )
      : undefined
  const subprocessCount = NodeEntity.getSubprocessCount(definition)

  const active = selected || highlighted
  // Process-instance audit palette (visited/current/pending) is the primary
  // signal in the read-only viewer, so it wins over readonly/active. Invalid
  // (drag error) still surfaces as red — it can't happen in the audit view
  // anyway, but preserves the safety net for the editor.
  const auditIconColor = auditStyles?.defaultIconColor ?? auditStyles?.defaultColor
  const borderColor = invalid
    ? GraphColors.red_500
    : auditStyles
      ? auditStyles.defaultColor
      : readonly
        ? GraphColors.neutral_500_90
        : active
          ? GraphColors.blue_500
          : GraphColors.neutral_500
  // Disabled nodes dim to 0.5; the hovered one un-dims
  const opacity = disabled && !hovered ? 0.5 : undefined
  const hoverHandlers = {
    onMouseEnter: () => setHovered(true),
    onMouseLeave: () => setHovered(false),
  }
  // Persistent warning icon top-right while the position is invalid
  const errorIcon = invalid && (
    <div
      data-testid={`bpmn-node-invalid-${definition.id}`}
      style={{
        position: 'absolute',
        right: 8,
        top: 8,
        width: 12,
        height: 12,
        display: 'flex',
        pointerEvents: 'none',
        zIndex: 1,
      }}
      dangerouslySetInnerHTML={{
        __html: recolorIconSvg(UtilityIcons.ERROR, GraphColors.red_500),
      }}
    />
  )

  // Subprocess badge: opens the subprocess list at the badge's right edge, in
  // canvas-container pixel coordinates.
  const openSubprocessesMenu = (event: React.SyntheticEvent<HTMLElement>): void => {
    event.stopPropagation()
    const badgeRect = event.currentTarget.getBoundingClientRect()
    const containerRect = event.currentTarget.closest('.react-flow')?.getBoundingClientRect()
    onShowSubprocessesMenu({
      node: definition,
      position: {
        x: badgeRect.left - (containerRect?.left ?? 0) + badgeRect.width / 2 + 16,
        y: badgeRect.top - (containerRect?.top ?? 0) + 8,
      },
    })
  }

  const subprocessBadge = subprocessCount > 0 && (
    <div
      data-testid={`bpmn-node-subprocesses-${definition.id}`}
      role="button"
      tabIndex={0}
      onClick={openSubprocessesMenu}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          openSubprocessesMenu(event)
        }
      }}
      style={{
        position: 'absolute',
        right: 4,
        bottom: 4,
        width: 24,
        height: 16,
        borderRadius: 4,
        background: GraphColors.neutral_100,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
        cursor: 'pointer',
        fontSize: 10,
        fontWeight: 600,
      }}
    >
      <span
        style={{ display: 'flex', width: 12, height: 12 }}
        dangerouslySetInnerHTML={{ __html: UtilityIcons.SUBPROCESSES }}
      />
      {subprocessCount}
    </div>
  )

  // Eye-slash marker on allowReadOnly versions (see mapper flag)
  const readonlyMarker = showReadonlyIcon && (
    <div
      data-testid={`bpmn-node-readonly-${definition.id}`}
      style={{
        position: 'absolute',
        right: 4,
        top: 4,
        width: 14,
        height: 14,
        display: 'flex',
        pointerEvents: 'none',
      }}
      dangerouslySetInnerHTML={{ __html: UtilityIcons.EYE_SLASH }}
    />
  )

  const commitRename = (newName: string): void => {
    setEditing(false)
    if (newName.trim() && newName !== definition.name) {
      onRenameNode({ node: definition, newName: newName.trim() })
    }
  }

  const startEditing = (): void => {
    if (!readonly) {
      setEditing(true)
    }
  }

  const handles = (
    <>
      {/* Anchor handles: existing edges attach here (no handle id on the edge) */}
      <Handle
        type="target"
        position={Position.Left}
        isConnectable={false}
        style={hiddenHandleStyle}
      />
      <Handle
        type="source"
        position={Position.Right}
        isConnectable={false}
        style={hiddenHandleStyle}
      />
      {!readonly && (
        <>
          {/* End nodes only take incoming sequences — no outgoing drag affordance.
              Non-gateway nodes lose it once they have their one outgoing sequence,
              so the affordance never offers a drag the drop would reject. */}
          {showConnectSource && (
            <Handle
              id={CONNECT_SOURCE_HANDLE_ID}
              type="source"
              position={Position.Right}
              isConnectableEnd={false}
              className="bpmn-connect-source-handle"
              data-testid={`bpmn-node-connect-${definition.id}`}
              // RF's own handle CSS (right: 0 + translate(50%, -50%)) centres
              // the icon on the border — half inside the node, half outside
              style={connectSourceHandleStyle}
            >
              <ConnectSourceIcon />
            </Handle>
          )}
          {/* One port per bounding-box side, all four mounted so React Flow has
              registered their bounds before a drag reaches the node — a diamond's
              vertices and a circle's cardinal points sit on those same four
              coordinates. Only the port the connection would land on is drawn. */}
          {TARGET_HANDLE_POSITIONS.map((position) => (
            <Handle
              key={position}
              id={targetHandleId(position)}
              type="target"
              position={position}
              isConnectableStart={false}
              data-testid={`bpmn-node-connect-target-${definition.id}${
                position === Position.Left ? '' : `-${position}`
              }`}
              style={{
                ...connectTargetHandleStyle,
                visibility: position === activeTargetPosition ? 'visible' : 'hidden',
              }}
            />
          ))}
        </>
      )}
    </>
  )

  if (visuals.kind === BpmnNodeKind.ACTIVITY) {
    const { padding, iconSize } = ActivityNodeStyles
    // The label box: the node minus a sidebar for the top-left type icon, and a
    // second one for the subprocess badge when it's there
    const labelWidth =
      visuals.width - (iconSize + padding * 2) - (subprocessCount > 0 ? 24 + padding * 2 : 0)

    return (
      <div
        data-testid={`bpmn-node-${definition.id}`}
        data-node-type={definition.type}
        onDoubleClick={startEditing}
        title={editing ? undefined : definition.name}
        {...hoverHandlers}
        style={{
          width: visuals.width,
          height: visuals.height,
          // SUBPROCESS_RUN gets a 3px border (getActivityNodeStyles)
          border: `${definition.type === ActivityNode.SUBPROCESS_RUN ? 3 : 1.5}px solid ${borderColor}`,
          borderRadius: 8,
          background: auditStyles
            ? auditStyles.neutralColor
            : readonly
              ? GraphColors.neutral_50
              : GraphColors.white,
          // Error shadow: hard red bar peeking shadowHeight above the node
          boxShadow: invalid
            ? `0 -4px 0 0 ${GraphColors.red_500}`
            : active
              ? `0 0 0 2px ${GraphColors.blue_500_20}`
              : undefined,
          // Pre-existing nodes dim to 0.5 while the AI draft preview is shown
          opacity,
          position: 'relative',
          cursor: 'pointer',
        }}
      >
        {handles}
        {editingUsers.length > 0 && <NodeEditingUsers users={editingUsers} />}
        {/* Icon and label are both absolute (
            updateLabelPosition): the icon pinned to the top-left corner, the
            label centred on the node itself rather than on what the icon
            leaves below it */}
        <div
          style={{
            position: 'absolute',
            top: padding,
            left: padding,
            width: iconSize,
            height: iconSize,
            color: borderColor,
          }}
          dangerouslySetInnerHTML={{
            __html: invalid
              ? recolorIconSvg(visuals.icon ?? '', GraphColors.red_500)
              : auditStyles
                ? recolorIconSvg(visuals.icon ?? '', auditIconColor)
                : readonly
                  ? recolorIconSvg(visuals.icon ?? '', GraphColors.neutral_500_90)
                  : (visuals.icon ?? ''),
          }}
        />
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
          }}
        >
          <NodeLabel
            nodeId={definition.id}
            name={definition.name}
            editing={editing}
            onCommit={commitRename}
            onCancel={() => setEditing(false)}
            width={labelWidth}
            maxHeight={visuals.height - padding * 2}
          />
        </div>
        {subprocessBadge}
        {readonlyMarker}
        {errorIcon}
      </div>
    )
  }

  // Error/disabled states recolor the SVG shape: base fill → state background,
  // rings/outlines + type icon → state color. Audit-state
  // palette takes precedence over readonly here for the same reason as the
  // activity path — it's the primary signal in the process-instance viewer.
  const shapeSvg = invalid
    ? recolorShapeSvg(visuals.shape ?? '', GraphColors.red_500, GraphColors.red_50)
    : auditStyles
      ? recolorShapeSvg(visuals.shape ?? '', auditStyles.defaultColor, auditStyles.neutralColor)
      : readonly
        ? recolorShapeSvg(visuals.shape ?? '', GraphColors.neutral_500_90, GraphColors.neutral_50)
        : (visuals.shape ?? '')
  const iconSvg = invalid
    ? recolorIconSvg(visuals.icon ?? '', GraphColors.red_500)
    : auditStyles
      ? recolorIconSvg(visuals.icon ?? '', auditIconColor)
      : readonly
        ? recolorIconSvg(visuals.icon ?? '', GraphColors.neutral_500_90)
        : visuals.icon

  return (
    <div
      data-testid={`bpmn-node-${definition.id}`}
      data-node-type={definition.type}
      onDoubleClick={startEditing}
      title={editing ? undefined : definition.name}
      {...hoverHandlers}
      style={{
        width: visuals.width,
        height: visuals.height,
        position: 'relative',
        cursor: 'pointer',
        opacity,
        filter: active ? `drop-shadow(0 0 3px ${GraphColors.blue_500})` : undefined,
      }}
    >
      {handles}
      {editingUsers.length > 0 && <NodeEditingUsers users={editingUsers} />}
      <div dangerouslySetInnerHTML={{ __html: shapeSvg }} />
      {iconSvg && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          dangerouslySetInnerHTML={{ __html: iconSvg }}
        />
      )}
      {errorIcon}
      <div
        style={{
          position: 'absolute',
          bottom: visuals.height + 4,
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          justifyContent: 'center',
          width: 140,
        }}
      >
        <NodeLabel
          nodeId={definition.id}
          name={definition.name}
          editing={editing}
          onCommit={commitRename}
          onCancel={() => setEditing(false)}
          width={NodeLabelStyles.width}
        />
      </div>
    </div>
  )
}

export default memo(BpmnFlowNode)
